import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLetterheadExport } from '../components/reports/useLetterheadExport';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { layoutForPath } from '../utils/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, ClipboardCheck, Wrench, AlertTriangle, CheckCircle2, Download, FileText, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DateField from '../components/DateField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAINT_TYPES = [
  { value: 'pm', label: 'Preventative Maintenance' },
  { value: 'calibration', label: 'Calibration' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'service', label: 'Service' },
  { value: 'emergency', label: 'Emergency Service' },
];
const typeLabel = (v) => MAINT_TYPES.find((t) => t.value === v)?.label || v;

const FINDINGS = [
  { value: 'pass', label: 'Pass' },
  { value: 'needs_work', label: 'Needs Work' },
  { value: 'fail', label: 'Fail' },
];

const emptyLog = {
  schedule_id: null,
  type: 'pm',
  performed_at: new Date().toISOString().slice(0, 10),
  performed_by_type: 'internal',
  performed_by_name: '',
  vendor_name: '',
  hours_at_performance: '',
  findings: 'pass',
  notes: '',
  document_url: '',
  next_due_at: '',
  next_due_hours: '',
};

const emptySchedule = {
  id: null,
  type: 'pm',
  interval_days: '',
  interval_hours: '',
  interval_uses: '',
  notes: '',
};

// Compute overdue-ness for a schedule
function scheduleStatus(sch, runtimeHours, useCount) {
  const today = new Date().toISOString().slice(0, 10);
  let overdueCalendar = false;
  let overdueHours = false;
  let overdueUses = false;
  if (sch.next_due_at && sch.next_due_at < today) overdueCalendar = true;
  if (sch.next_due_hours != null && runtimeHours != null && Number(runtimeHours) >= Number(sch.next_due_hours)) {
    overdueHours = true;
  }
  if (sch.next_due_uses != null && useCount != null && Number(useCount) >= Number(sch.next_due_uses)) {
    overdueUses = true;
  }
  return { overdue: overdueCalendar || overdueHours || overdueUses, overdueCalendar, overdueHours, overdueUses };
}

export default function EquipmentDetail() {
  const { requestExport, exportFeedback, exporting } = useLetterheadExport('equipment-letterhead-export');
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const listPath = useLocation().pathname.startsWith('/admin/') ? '/admin/equipment' : '/equipment';
  const Layout = layoutForPath(useLocation().pathname);
  const [eq, setEq] = useState(null);
  const [logs, setLogs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showLogDialog, setShowLogDialog] = useState(false);
  const [logForm, setLogForm] = useState(emptyLog);
  const [editingLogId, setEditingLogId] = useState(null);

  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(emptySchedule);
  const [editingScheduleIdx, setEditingScheduleIdx] = useState(null);

  const canManageOrForeman = user?.role === 'management' || user?.role === 'foreman'
    || (user?.effective_permissions || []).includes('equipment.edit');
  const canManage = user?.role === 'management' || (user?.effective_permissions || []).includes('equipment.edit');

  const loadAll = useCallback(async () => {
    try {
      const [eqRes, listRes, logsRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/equipment`),
        axios.get(`${API}/equipment`),
        axios.get(`${API}/equipment/${id}/maintenance-logs`),
        axios.get(`${API}/vendors`),
      ]);
      // find matching equipment (there's no GET /equipment/{id} — list scans work fine for small sets)
      const match = (eqRes.data || []).find((e) => e.id === id);
      if (!match) {
        toast.error('Equipment not found');
        navigate(listPath);
        return;
      }
      setEq(match);
      setLogs(logsRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch {
      toast.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // --------- Schedule handlers ----------
  const openAddSchedule = () => {
    setEditingScheduleIdx(null);
    setScheduleForm(emptySchedule);
    setShowScheduleDialog(true);
  };

  const openEditSchedule = (idx) => {
    const s = eq.maintenance_schedules[idx];
    setEditingScheduleIdx(idx);
    setScheduleForm({
      id: s.id,
      type: s.type,
      interval_days: s.interval_days ?? '',
      interval_hours: s.interval_hours ?? '',
      interval_uses: s.interval_uses ?? '',
      notes: s.notes ?? '',
    });
    setShowScheduleDialog(true);
  };

  const saveSchedule = async (e) => {
    e.preventDefault();
    const days = scheduleForm.interval_days === '' ? null : Number(scheduleForm.interval_days);
    const hours = scheduleForm.interval_hours === '' ? null : Number(scheduleForm.interval_hours);
    const uses = scheduleForm.interval_uses === '' ? null : Number(scheduleForm.interval_uses);
    if (!days && !hours && !uses) {
      toast.error('Enter a calendar interval, runtime-hours interval, use-count interval, or any combination.');
      return;
    }
    const newEntry = {
      id: scheduleForm.id || undefined,
      type: scheduleForm.type,
      interval_days: days,
      interval_hours: hours,
      interval_uses: uses,
      notes: scheduleForm.notes || null,
    };
    let arr = [...(eq.maintenance_schedules || [])];
    if (editingScheduleIdx != null) {
      // preserve last_performed_* + next_due_* from prior entry
      const prior = arr[editingScheduleIdx];
      arr[editingScheduleIdx] = { ...prior, ...newEntry };
    } else {
      arr.push(newEntry);
    }
    try {
      const res = await axios.put(`${API}/equipment/${id}/maintenance-schedules`, arr);
      setEq({ ...eq, maintenance_schedules: res.data.maintenance_schedules });
      toast.success(editingScheduleIdx != null ? 'Schedule updated' : 'Schedule added');
      setShowScheduleDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    }
  };

  const deleteSchedule = async (idx) => {
    if (!window.confirm('Delete this maintenance schedule?')) return;
    const arr = [...(eq.maintenance_schedules || [])];
    arr.splice(idx, 1);
    try {
      const res = await axios.put(`${API}/equipment/${id}/maintenance-schedules`, arr);
      setEq({ ...eq, maintenance_schedules: res.data.maintenance_schedules });
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  // --------- Log handlers ----------
  const openLogFromSchedule = (schedule) => {
    setEditingLogId(null);
    setLogForm({
      ...emptyLog,
      schedule_id: schedule.id,
      type: schedule.type,
      performed_by_name: user?.name || '',
      hours_at_performance: eq.runtime_hours != null ? String(eq.runtime_hours) : '',
    });
    setShowLogDialog(true);
  };

  const openLogGeneric = () => {
    setEditingLogId(null);
    setLogForm({
      ...emptyLog,
      performed_by_name: user?.name || '',
      hours_at_performance: eq.runtime_hours != null ? String(eq.runtime_hours) : '',
    });
    setShowLogDialog(true);
  };

  const openEditLog = (log) => {
    setEditingLogId(log.id);
    setLogForm({
      schedule_id: log.schedule_id || null,
      type: log.type,
      performed_at: log.performed_at,
      performed_by_type: log.performed_by_type || 'internal',
      performed_by_name: log.performed_by_name || '',
      vendor_name: log.vendor_name || '',
      hours_at_performance: log.hours_at_performance != null ? String(log.hours_at_performance) : '',
      findings: log.findings || 'pass',
      notes: log.notes || '',
      document_url: log.document_url || '',
      next_due_at: log.next_due_at || '',
      next_due_hours: log.next_due_hours != null ? String(log.next_due_hours) : '',
    });
    setShowLogDialog(true);
  };

  const saveLog = async (e) => {
    e.preventDefault();
    const payload = {
      schedule_id: logForm.schedule_id || null,
      type: logForm.type,
      performed_at: logForm.performed_at,
      performed_by_type: logForm.performed_by_type,
      performed_by_name: (logForm.performed_by_type === 'vendor' ? logForm.vendor_name : logForm.performed_by_name).trim(),
      vendor_name: logForm.performed_by_type === 'vendor' ? (logForm.vendor_name || '').trim() : null,
      hours_at_performance: logForm.hours_at_performance !== '' ? Number(logForm.hours_at_performance) : null,
      findings: logForm.findings,
      notes: logForm.notes || null,
      document_url: logForm.document_url || null,
      next_due_at: logForm.next_due_at || null,
      next_due_hours: logForm.next_due_hours !== '' ? Number(logForm.next_due_hours) : null,
    };
    if (!payload.performed_by_name) {
      toast.error(logForm.performed_by_type === 'vendor' ? 'Vendor name required' : 'Performed-by name required');
      return;
    }
    try {
      if (editingLogId) {
        await axios.patch(`${API}/maintenance-logs/${editingLogId}`, payload);
        toast.success('Log updated');
      } else {
        await axios.post(`${API}/equipment/${id}/maintenance-logs`, payload);
        toast.success('Log recorded');
      }
      setShowLogDialog(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save log');
    }
  };

  const deleteLog = async (logId) => {
    if (!window.confirm('Delete this maintenance log?')) return;
    try {
      await axios.delete(`${API}/maintenance-logs/${logId}`);
      toast.success('Deleted');
      loadAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const downloadCompliance = async (fmt) => {
    try {
      const res = await axios.get(`${API}/equipment/${id}/compliance-export?format=${fmt}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-${(eq?.name || 'equipment').replace(/[^A-Za-z0-9_-]+/g, '-')}-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (fmt === 'pdf') throw error;
      toast.error('Export failed');
    }
  };

  const syncFromHistory = async () => {
    try {
      const res = await axios.post(`${API}/equipment/${id}/maintenance-schedules/sync-from-logs`);
      const { updated_schedules, linked_logs } = res.data;
      if (updated_schedules === 0) {
        toast.info('Nothing to sync — no matching logs found');
      } else {
        toast.success(`Synced ${updated_schedules} schedule${updated_schedules === 1 ? '' : 's'} from ${linked_logs} historical log${linked_logs === 1 ? '' : 's'}`);
      }
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    }
  };

  const vendorSuggestions = useMemo(
    () => vendors.map((v) => v.name).filter(Boolean),
    [vendors]
  );

  if (loading) return <Layout><div className="p-8 text-slate-500">Loading…</div></Layout>;
  if (!eq) return null;

  const schedules = eq.maintenance_schedules || [];
  const overdueCount = schedules.filter((s) => scheduleStatus(s, eq.runtime_hours, eq.use_count).overdue).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="equipment-detail-page">
        <div>
          <button
            onClick={() => navigate(listPath)}
            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-3"
            data-testid="back-to-equipment"
          >
            <ArrowLeft size={16} /> Back to Equipment
          </button>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                {eq.name}
                {eq.billable === false && (
                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border border-slate-300 bg-slate-100 text-slate-700" data-testid="detail-non-billable-pill">
                    Non-billable
                  </span>
                )}
              </h2>
              <div className="mt-2 text-sm text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
                <span>Status: <span className="font-semibold text-slate-800 capitalize">{(eq.status || 'available').replace('_', ' ')}</span></span>
                <span>Cost: <span className="font-mono">${eq.cost?.toFixed(2)}</span> / {eq.cost_type?.replace('_', ' ')}</span>
                <span>Runtime hours: <span className="font-mono" data-testid="detail-runtime-hours">{(eq.runtime_hours || 0).toFixed(1)}</span></span>
                <span>Uses: <span className="font-mono" data-testid="detail-use-count">{eq.use_count || 0}</span></span>
              </div>
              {(eq.make || eq.model || eq.serial_number || eq.purchase_date) && (
                <div className="mt-2 text-sm text-slate-600 flex flex-wrap gap-x-6 gap-y-1" data-testid="equipment-meta">
                  {eq.make && <span>Make: <span className="font-semibold text-slate-800">{eq.make}</span></span>}
                  {eq.model && <span>Model: <span className="font-semibold text-slate-800">{eq.model}</span></span>}
                  {eq.serial_number && <span>S/N: <span className="font-mono text-slate-800">{eq.serial_number}</span></span>}
                  {eq.purchase_date && <span>Purchased: <span className="font-mono text-slate-800">{eq.purchase_date}</span></span>}
                </div>
              )}
            </div>
            {overdueCount > 0 && (
              <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="overdue-banner">
                <AlertTriangle size={18} />
                <span className="text-sm font-medium">
                  {overdueCount} maintenance item{overdueCount === 1 ? '' : 's'} overdue
                </span>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadCompliance('csv')} data-testid="eq-export-csv">
              <Download size={14} className="mr-1" /> Compliance CSV
            </Button>
            <Button size="sm" variant="outline" disabled={exporting} onClick={() => requestExport(() => downloadCompliance('pdf'))} data-testid="eq-export-pdf">
              <FileText size={14} className="mr-1" /> Compliance PDF
            </Button>
            {exportFeedback}
          </div>
        </div>

        <Tabs defaultValue="schedules">
          <TabsList>
            <TabsTrigger value="schedules" data-testid="tab-schedules"><ClipboardCheck size={14} className="mr-1.5" />Schedules</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history"><Wrench size={14} className="mr-1.5" />History</TabsTrigger>
          </TabsList>

          {/* --- Schedules Tab --- */}
          <TabsContent value="schedules" className="space-y-4 mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-slate-600">Recurring maintenance rules. Due when EITHER the calendar interval OR the runtime-hours threshold is hit.</p>
              <div className="flex gap-2">
                {canManageOrForeman && schedules.length > 0 && logs.length > 0 && (
                  <Button onClick={syncFromHistory} size="sm" variant="outline" data-testid="sync-schedules-button" title="Rebuild schedule dates from the maintenance log history">
                    <RefreshCw size={14} className="mr-1" /> Sync from History
                  </Button>
                )}
                {canManageOrForeman && (
                  <Button onClick={openAddSchedule} size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="add-schedule-button">
                    <Plus size={16} className="mr-1" /> Add Schedule
                  </Button>
                )}
              </div>
            </div>

            {schedules.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500">
                No maintenance schedules yet.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {schedules.map((s, idx) => {
                const status = scheduleStatus(s, eq.runtime_hours, eq.use_count);
                return (
                  <div
                    key={s.id || idx}
                    className={`bg-white border rounded-md p-4 ${status.overdue ? 'border-amber-400' : 'border-slate-200'}`}
                    data-testid={`schedule-card-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-bold text-slate-900">{typeLabel(s.type)}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {s.interval_days ? <span>Every {s.interval_days} days</span> : null}
                          {s.interval_days && (s.interval_hours || s.interval_uses) ? <span className="mx-1">·</span> : null}
                          {s.interval_hours ? <span>Every {s.interval_hours} hrs</span> : null}
                          {s.interval_hours && s.interval_uses ? <span className="mx-1">·</span> : null}
                          {s.interval_uses ? <span>Every {s.interval_uses} uses</span> : null}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {canManageOrForeman && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => openLogFromSchedule(s)} data-testid={`log-schedule-${idx}`} title="Log completion">
                              <CheckCircle2 size={16} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditSchedule(idx)} data-testid={`edit-schedule-${idx}`}>Edit</Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteSchedule(idx)} data-testid={`delete-schedule-${idx}`}>
                              <Trash2 size={16} />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 space-y-1 text-xs">
                      <div className="text-slate-600">
                        Last done:{' '}
                        <span className="font-medium text-slate-800">
                          {s.last_performed_at || '—'}
                          {s.last_performed_hours != null && <span className="text-slate-500"> @ {s.last_performed_hours} hrs</span>}
                          {s.last_performed_uses != null && <span className="text-slate-500"> @ {s.last_performed_uses} uses</span>}
                        </span>
                      </div>
                      <div>
                        Next due:{' '}
                        <span className={`font-semibold ${status.overdue ? 'text-amber-700' : 'text-slate-800'}`}>
                          {s.next_due_at || '—'}
                          {s.next_due_hours != null && <span> · {s.next_due_hours} hrs</span>}
                          {s.next_due_uses != null && <span> · {s.next_due_uses} uses</span>}
                        </span>
                        {status.overdue && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase">
                            Overdue
                          </span>
                        )}
                      </div>
                      {s.notes && <div className="text-slate-500 italic mt-1">{s.notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* --- History Tab --- */}
          <TabsContent value="history" className="space-y-3 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">Every logged maintenance event on this equipment.</p>
              {canManageOrForeman && (
                <Button onClick={openLogGeneric} size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="add-log-button">
                  <Plus size={16} className="mr-1" /> Log Maintenance
                </Button>
              )}
            </div>

            {logs.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500">
                No maintenance history yet.
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="maintenance-logs-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Type</th>
                    <th className="text-left px-4 py-2">Performed By</th>
                    <th className="text-left px-4 py-2">Labor</th>
                    <th className="text-left px-4 py-2">Runtime @</th>
                    <th className="text-left px-4 py-2">Findings</th>
                    <th className="text-left px-4 py-2">Notes</th>
                    {canManageOrForeman && <th className="text-left px-4 py-2">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {logs.map((l) => {
                    const isShopTime = l.source === 'shop_time';
                    const laborDisplay = isShopTime
                      ? (
                          <span>
                            <span className="font-mono">{l.shop_hours}</span>
                            {l.shop_overtime_hours > 0 && (
                              <span className="ml-1 text-xs text-amber-700 font-semibold">+{l.shop_overtime_hours} OT</span>
                            )}
                            <span className="ml-1 text-[10px] text-slate-500">hrs</span>
                          </span>
                        )
                      : <span className="text-slate-400">—</span>;
                    const runtimeDisplay = l.hours_at_performance != null
                      ? <span><span className="font-mono">{l.hours_at_performance}</span><span className="ml-1 text-[10px] text-slate-500">hrs</span></span>
                      : <span className="text-slate-400">—</span>;
                    return (
                    <tr key={`${l.source || 'log'}-${l.id}`} data-testid={`log-row-${l.id}`}>
                      <td className="px-4 py-2 font-mono text-slate-700">{l.performed_at}</td>
                      <td className="px-4 py-2">
                        {typeLabel(l.type)}
                        {isShopTime && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-bold uppercase">Shop Time</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-slate-800">{l.performed_by_name}</div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500">{l.performed_by_type === 'vendor' ? 'Vendor' : 'Internal'}</div>
                      </td>
                      <td className="px-4 py-2 font-mono text-slate-700">{laborDisplay}</td>
                      <td className="px-4 py-2 font-mono text-slate-700">{runtimeDisplay}</td>
                      <td className="px-4 py-2">
                        {isShopTime ? (
                          <span className="text-slate-400 text-xs">—</span>
                        ) : (
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${
                            l.findings === 'pass' ? 'bg-green-50 border-green-200 text-green-800' :
                            l.findings === 'needs_work' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                            'bg-red-50 border-red-200 text-red-800'
                          }`}>
                            {FINDINGS.find((f) => f.value === l.findings)?.label || l.findings}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-600 max-w-xs truncate" title={l.notes || ''}>{l.notes || '—'}</td>
                      {canManageOrForeman && (
                        <td className="px-4 py-2">
                          {isShopTime ? (
                            <button
                              type="button"
                              onClick={() => navigate('/shop-time')}
                              className="text-xs text-blue-600 hover:underline"
                              data-testid={`open-shop-time-${l.id}`}
                              title="Edit on Shop Time page"
                            >
                              Shop Time →
                            </button>
                          ) : (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openEditLog(l)} data-testid={`edit-log-${l.id}`}>Edit</Button>
                              {canManage && (
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteLog(l.id)} data-testid={`delete-log-${l.id}`}>
                                  <Trash2 size={14} />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>

        {/* ------- Log Maintenance Dialog ------- */}
        <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingLogId ? 'Edit Maintenance Log' : 'Log Maintenance'}</DialogTitle>
              <DialogDescription>Records a completed maintenance event. Restricted to Management and Foreman.</DialogDescription>
            </DialogHeader>
            <form onSubmit={saveLog} className="space-y-3" data-testid="log-form">
              {schedules.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Link to schedule (optional)</Label>
                  <Select
                    value={logForm.schedule_id || '__none'}
                    onValueChange={(v) => {
                      const chosen = v === '__none' ? null : v;
                      // If a schedule is chosen, auto-align the log's type to it.
                      const sch = schedules.find((s) => s.id === chosen);
                      setLogForm({ ...logForm, schedule_id: chosen, type: sch ? sch.type : logForm.type });
                    }}
                  >
                    <SelectTrigger data-testid="log-schedule-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— Auto-detect from type —</SelectItem>
                      {schedules.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {typeLabel(s.type)}
                          {s.interval_days ? ` · every ${s.interval_days}d` : ''}
                          {s.interval_hours ? ` · every ${s.interval_hours}h` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500">Linking updates the schedule&apos;s Last Done / Next Due automatically.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={logForm.type} onValueChange={(v) => setLogForm({ ...logForm, type: v })}>
                    <SelectTrigger data-testid="log-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MAINT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Performed On</Label>
                  <DateField value={logForm.performed_at} onChange={(v) => setLogForm({ ...logForm, performed_at: v })} data-testid="log-date" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Performed By</Label>
                <div className="flex gap-2">
                  <Select value={logForm.performed_by_type} onValueChange={(v) => setLogForm({ ...logForm, performed_by_type: v })}>
                    <SelectTrigger className="w-32" data-testid="log-perf-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Internal</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                    </SelectContent>
                  </Select>
                  {logForm.performed_by_type === 'vendor' ? (
                    <div className="flex-1">
                      <Input
                        list="vendor-suggestions"
                        placeholder="Vendor name"
                        value={logForm.vendor_name}
                        onChange={(e) => setLogForm({ ...logForm, vendor_name: e.target.value })}
                        data-testid="log-vendor-name"
                      />
                      <datalist id="vendor-suggestions">
                        {vendorSuggestions.map((n) => <option key={n} value={n} />)}
                      </datalist>
                    </div>
                  ) : (
                    <Input
                      placeholder="Technician / staff name"
                      value={logForm.performed_by_name}
                      onChange={(e) => setLogForm({ ...logForm, performed_by_name: e.target.value })}
                      className="flex-1"
                      data-testid="log-performer-name"
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Runtime Hours at Service</Label>
                  <Input type="number" step="0.1" min="0" value={logForm.hours_at_performance} onChange={(e) => setLogForm({ ...logForm, hours_at_performance: e.target.value })} data-testid="log-hours" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Findings</Label>
                  <Select value={logForm.findings} onValueChange={(v) => setLogForm({ ...logForm, findings: v })}>
                    <SelectTrigger data-testid="log-findings"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FINDINGS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Next Due Date (optional)</Label>
                  <DateField value={logForm.next_due_at} onChange={(v) => setLogForm({ ...logForm, next_due_at: v })} data-testid="log-next-due-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Next Due Runtime Hrs (optional)</Label>
                  <Input type="number" step="0.1" min="0" value={logForm.next_due_hours} onChange={(e) => setLogForm({ ...logForm, next_due_hours: e.target.value })} data-testid="log-next-due-hours" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Certification / Doc URL (optional)</Label>
                <Input placeholder="https://…" value={logForm.document_url} onChange={(e) => setLogForm({ ...logForm, document_url: e.target.value })} data-testid="log-doc-url" />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea rows={3} value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} data-testid="log-notes" />
              </div>

              <p className="text-[11px] text-slate-500">
                Blank Next-Due fields auto-derive from the linked schedule&apos;s interval.
              </p>

              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-log">
                {editingLogId ? 'Update Log' : 'Save Log'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ------- Schedule Dialog ------- */}
        <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingScheduleIdx != null ? 'Edit Schedule' : 'Add Maintenance Schedule'}</DialogTitle>
              <DialogDescription>Set a calendar interval, runtime-hours interval, or both.</DialogDescription>
            </DialogHeader>
            <form onSubmit={saveSchedule} className="space-y-3" data-testid="schedule-form">
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={scheduleForm.type} onValueChange={(v) => setScheduleForm({ ...scheduleForm, type: v })}>
                  <SelectTrigger data-testid="schedule-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MAINT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Every N Days</Label>
                  <Input type="number" min="1" step="1" value={scheduleForm.interval_days} onChange={(e) => setScheduleForm({ ...scheduleForm, interval_days: e.target.value })} data-testid="schedule-interval-days" placeholder="e.g. 90" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Every N Hours</Label>
                  <Input type="number" min="1" step="0.5" value={scheduleForm.interval_hours} onChange={(e) => setScheduleForm({ ...scheduleForm, interval_hours: e.target.value })} data-testid="schedule-interval-hours" placeholder="e.g. 250" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Every N Uses</Label>
                  <Input type="number" min="1" step="1" value={scheduleForm.interval_uses} onChange={(e) => setScheduleForm({ ...scheduleForm, interval_uses: e.target.value })} data-testid="schedule-interval-uses" placeholder="e.g. 50" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">Fill in one or more. Whichever comes first will trigger the &quot;Overdue&quot; flag.</p>
              <div className="space-y-1">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea rows={2} value={scheduleForm.notes} onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })} data-testid="schedule-notes" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-schedule">
                {editingScheduleIdx != null ? 'Update Schedule' : 'Add Schedule'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
